/**
 * Pure helpers for the recursive condition tree editor.
 * Tree shape mirrors greenhouse-api scenario.settings.conditions:
 *   branch: {and|or: Node[]} or {not: Node}
 *   leaf:   {input: string, cmp: string, value: number|string|boolean}
 *
 * Paths are arrays of indices into branch children, with `'not'` for the
 * inner subtree of a `not` branch.
 *
 * Example paths into {and: [{or: [LeafA, LeafB]}, LeafC]}:
 *   []         → the root and-branch
 *   [0]        → the or-branch
 *   [0, 1]     → LeafB
 *   [1]        → LeafC
 */

export const COMPARATORS = ['=', '!=', '<', '<=', '>', '>=']

export const BRANCH_OPS = ['and', 'or', 'not']

/**
 * @return {{input: string, cmp: string, value: number}}
 */
export function defaultLeaf() {
    return {input: '', cmp: '>', value: 0}
}

/**
 * @param {'and'|'or'|'not'} op
 * @return {Object}
 */
export function defaultGroup(op = 'and') {
    if (op === 'not')
        return {not: defaultLeaf()}
    return {[op]: [defaultLeaf()]}
}

/**
 * @param {Object|null|undefined} node
 * @return {boolean}
 */
export function isLeaf(node) {
    if (!node || typeof node !== 'object')
        return false
    return !('and' in node) && !('or' in node) && !('not' in node)
}

/**
 * @param {Object} node
 * @return {'and'|'or'|'not'|null}
 */
export function branchOp(node) {
    if (!node) return null
    if (node.and) return 'and'
    if (node.or) return 'or'
    if (node.not) return 'not'
    return null
}

/**
 * @param {Object} node
 * @param {Array<number|'not'>} path
 * @return {Object|null}
 */
export function getAtPath(node, path) {
    let cursor = node
    for (const step of path) {
        if (cursor == null) return null
        if (step === 'not') {
            cursor = cursor.not
        } else if (cursor.and) {
            cursor = cursor.and[step]
        } else if (cursor.or) {
            cursor = cursor.or[step]
        } else {
            return null
        }
    }
    return cursor
}

/**
 * Deep-clones the tree but only along the path of mutation.
 * @param {Object} root
 * @param {Array<number|'not'>} path
 * @param {(node: Object) => Object|null} mutate  returns the replacement node, or null to delete
 * @return {Object|null}
 */
function modifyAtPath(root, path, mutate) {
    if (path.length === 0)
        return mutate(root)
    if (!root)
        return root
    const [head, ...rest] = path
    if (head === 'not') {
        const child = modifyAtPath(root.not, rest, mutate)
        if (child === null)
            return null
        return {...root, not: child}
    }
    const op = branchOp(root)
    if (op !== 'and' && op !== 'or')
        return root
    const children = root[op]
    if (rest.length === 0) {
        const replacement = mutate(children[head])
        const next = replacement === null
            ? children.filter((_, i) => i !== head)
            : children.map((c, i) => (i === head ? replacement : c))
        return {[op]: next}
    }
    const child = modifyAtPath(children[head], rest, mutate)
    if (child === null) {
        const next = children.filter((_, i) => i !== head)
        return {[op]: next}
    }
    return {[op]: children.map((c, i) => (i === head ? child : c))}
}

/**
 * @param {Object} root
 * @param {Array<number|'not'>} path
 * @param {Object} replacement
 */
export function replaceAtPath(root, path, replacement) {
    return modifyAtPath(root, path, () => replacement)
}

/**
 * @param {Object} root
 * @param {Array<number|'not'>} path
 */
export function removeAtPath(root, path) {
    return modifyAtPath(root, path, () => null)
}

/**
 * Append a new child (leaf or group) to the branch at the given path.
 * If the target is a leaf, no-op.
 * @param {Object} root
 * @param {Array<number|'not'>} path
 * @param {Object} child
 */
export function addChildAtPath(root, path, child) {
    return modifyAtPath(root, path, (node) => {
        const op = branchOp(node)
        if (op === 'and' || op === 'or')
            return {[op]: [...node[op], child]}
        if (op === 'not')
            return {not: child}
        return node
    })
}

/**
 * Change the operator of the branch at path. `not` wraps the first child,
 * `and`/`or` lifts the inner node into a one-child array.
 * @param {Object} root
 * @param {Array<number|'not'>} path
 * @param {'and'|'or'|'not'} nextOp
 */
export function toggleOp(root, path, nextOp) {
    return modifyAtPath(root, path, (node) => {
        const op = branchOp(node)
        if (!op || op === nextOp)
            return node
        if (nextOp === 'not') {
            const firstChild = op === 'not' ? node.not : node[op][0]
            return {not: firstChild || defaultLeaf()}
        }
        const seed = op === 'not' ? [node.not] : node[op]
        return {[nextOp]: seed}
    })
}

/**
 * @param {Object} node
 * @return {number}
 */
function depthOf(node) {
    if (!node)
        return 0
    const op = branchOp(node)
    if (op === 'and' || op === 'or') {
        const childDepths = node[op].map(depthOf)
        return 1 + (childDepths.length === 0 ? 0 : Math.max(...childDepths))
    }
    if (op === 'not')
        return 1 + depthOf(node.not)
    return 1
}

/**
 * @param {Object} node
 * @return {number}
 */
function countLeaves(node) {
    if (!node)
        return 0
    if (isLeaf(node))
        return 1
    const op = branchOp(node)
    if (op === 'not')
        return countLeaves(node.not)
    return node[op].reduce((acc, child) => acc + countLeaves(child), 0)
}

/**
 * @param {Object} tree
 * @param {{maxDepth?: number}} [opts]
 * @return {{ok: boolean, depth: number, leafCount: number, error?: string}}
 */
export function validateTree(tree, opts = {}) {
    const maxDepth = opts.maxDepth ?? 8
    const depth = depthOf(tree)
    const leafCount = countLeaves(tree)
    if (!tree)
        return {ok: false, depth: 0, leafCount: 0, error: 'Tree is empty.'}
    if (depth > maxDepth)
        return {ok: false, depth, leafCount, error: `Tree depth ${depth} exceeds limit of ${maxDepth}.`}
    if (leafCount === 0)
        return {ok: false, depth, leafCount, error: 'Tree has no leaves.'}
    return {ok: true, depth, leafCount}
}
